import * as focusTrap from 'focus-trap';
import LuminisServiceUtils from "@js/utils/luminis-service-utils.es6.js";
import OverlayService from "@js/overlay-service.es6.js";

export default class DashboardEditAppointmentModal {
  constructor(visitJson, updateCallback) {
    this.originalHTML = document.querySelector(
      '.edit-appointment-modal',
    ).outerHTML;

    this.setEditIconClickable();

    this.trap = null;
    this.overlayService = new OverlayService();
    this.visitJson = visitJson;
    this.luminisServiceUtils = new LuminisServiceUtils();
    this.activeVisit = null;
    this.updateCallback = updateCallback;
    this.errorRedirectPage = document.querySelector(
      '.field--name-field-error-redirect-page a',
    );
  }

  setVisitDetails(visitInfo) {
    this.visitType = visitInfo.querySelector(
      '.visit-type',
    ).innerHTML;
    this.visitTime = visitInfo.querySelector(
      '.visit-time',
    ).innerHTML;
    this.month = visitInfo.querySelector(
      '.date .month',
    ).innerHTML;
    this.day = visitInfo.querySelector(
      '.date .day',
    ).innerHTML;
  }

  setEditIconClickable() {
    document
      .querySelectorAll('.appointment .edit-icon')
      .forEach(apt => {
        // Add click event listener
        apt.addEventListener('click', event => {
          this.setActiveAppt(apt, event);
        });

        // Add keydown event listener
        apt.addEventListener('keydown', event => {
          if (event.key === 'Enter' || event.code === 'Enter') {
            this.setActiveAppt(apt, event);
          }
        });
      });
  }

  setActiveAppt(apt, event) {
    let visitCard;
    // if a list of visits look up active card
    if (Array.isArray(this.visitJson)) {
      const csn = apt.getAttribute('data-csn');
      this.activeVisit = this.visitJson.find(item => item.csn === csn);
      const editIconElement = event.target;
      visitCard = editIconElement.closest('.visit-card');
    } else {
      this.activeVisit = this.visitJson;
      visitCard = document.querySelector(
        '.visits .appointment');
    }

    this.setVisitDetails(visitCard);
    this.showEditAppointmentModal();
  }

  showEditAppointmentModal() {
    // reset html if the user has already tried to use the modal
    document.querySelector(
      '.edit-appointment-modal',
    ).outerHTML = this.originalHTML;

    this.createModalClickEvents();
    const modal = document.querySelector('.edit-appointment-modal');
    modal.classList.toggle('hidden');

    modal.querySelector('.month').innerHTML = this.month;
    modal.querySelector('.day').innerHTML = this.day;
    modal.querySelector('.visit-type').innerHTML = this.visitType;
    modal.querySelector('.visit-time').innerHTML = this.visitTime;

    const onOverlayClick = () => {
      // Even though this is a one-liner, we need this in an anonymous function
      // to ensure "this" will refer to the correct object when passed to toggleOverlay
      this.closeModal();
    };

    this.overlayService.toggleOverlay(true, onOverlayClick, true);
    this.trap = focusTrap.createFocusTrap('.edit-appointment-modal', {
      initialFocus: '.close-modal',
      clickOutsideDeactivates: 'true',
    });
    this.trap.activate();
  }

  createModalClickEvents() {
    const modal = document.querySelector('.edit-appointment-modal');
    const closeModalButton = modal.querySelector('.close-modal');
    const continueButton = modal.querySelector('.modal__continue');
    const radioButtons = modal.querySelectorAll(
      'input[name="appointment-option"]',
    );
    this.handleRadioButtons(radioButtons, continueButton);
    this.handleContinueButton(continueButton);
    this.setupModalCloseActions(modal, closeModalButton);
    this.setupModalBackButton();
    const callButton = document.querySelector('.modal__call');
    callButton.innerHTML = `${callButton.innerHTML}&nbsp;${this.activeVisit.department.phone}`;
    callButton.addEventListener('click', () => {
      window.location.href = `tel:${this.activeVisit.department.phone}`;
    });
  }

  handleRadioButtons(radioButtons, continueButton) {
    radioButtons.forEach(radio => {
      radio.addEventListener('click', event => {
        const parentOption = radio.closest('.edit-appointment-modal__option');
        this.toggleRadioButton(event, parentOption, continueButton);
      });
      radio.addEventListener('keydown', event => {
        if (event.key === 'Enter' || event.code === 'Enter') {
          const parentOption = radio.closest('.edit-appointment-modal__option');
          this.toggleRadioButton(event, parentOption, continueButton);
        }
      });
    });
  }

  toggleRadioButton(event, parentOption, continueButton) {
    if (event.target.checked && this.selectedOption === event.target.value) {
      event.target.checked = false;
      this.selectedOption = null;
      continueButton.disabled = true;
      parentOption.classList.remove('-selected');
    } else {
      document
        .querySelectorAll('.edit-appointment-modal__option')
        .forEach(option => {
          option.classList.remove('-selected');
        });

      this.selectedOption = event.target.value;
      continueButton.disabled = false;
      parentOption.classList.add('-selected');
    }
  }

  handleContinueButton(continueButton) {
    continueButton.addEventListener('click', () => {
      if (this.selectedOption === 'reschedule') {
        if (this.activeVisit.rescheduleAllowed && this.activeVisit.rescheduleUrl) {
          window.location = this.activeVisit.rescheduleUrl;
        } else {
          this.showOnlineChangesUnavailable();
        }
      } else if (this.selectedOption === 'cancel') {
        if (this.activeVisit.cancelAllowed) {
          const cancelConfirmation = document.querySelector(
            '.field--name-field-confirmation-question .field__item',
          ).innerHTML;
          this.updateModalView(cancelConfirmation);
        } else {
          this.showOnlineChangesUnavailable();
        }
      }
    });
  }

  showOnlineChangesUnavailable() {
    const onlineChangesUnavailable = document.querySelector(
      '.field--name-field-online-changes-unavailable .field__item',
    ).innerHTML;
    this.updateModalView(onlineChangesUnavailable);
    const continueButton = document.querySelector('.modal__continue');
    continueButton.classList.add('hidden');
    const callButton = document.querySelector('.modal__call');
    callButton.classList.remove('hidden');
  }

  setupModalCloseActions(modal, closeModalButton) {
    closeModalButton.addEventListener('click', () => {
      this.overlayService.toggleOverlay(false);
      this.trap.deactivate();
      this.closeModal();
    });

    modal.addEventListener('keydown', event => {
      if (event.key === 'Escape') {
        this.overlayService.toggleOverlay(false);
        this.trap.deactivate();
        this.closeModal();
      }
    });
  }

  setupModalBackButton() {
    const backButton = document.querySelector(
      '.edit-appointment-modal .back-link',
    );
    backButton.addEventListener('click', () => {
      this.trap.deactivate();
      this.showEditAppointmentModal();
    });
  }

  closeModal() {
    const modal = document.querySelector('.edit-appointment-modal');
    const editIcon = modal.querySelector('.edit-icon');
    modal.classList.add('hidden');
    if (editIcon) {
      editIcon.focus();
    }

    this.overlayService.toggleOverlay(false);
  }

  updateModalView(newTitle) {
    document
      .querySelector('.edit-appointment-modal__content .back-link')
      .classList.remove('hidden');
    document.querySelector(
      '.edit-appointment-modal__heading',
    ).textContent = newTitle;

    const optionsElement = document.querySelector(
      '.edit-appointment-modal__options',
    );
    if (optionsElement) {
      optionsElement.remove();
    }

    const continueButton = document.querySelector('.modal__continue');
    continueButton.disabled = false;

    continueButton.addEventListener('click', this.confirmAction.bind(this), {
      once: true,
    });
    this.trap.updateContainerElements('.edit-appointment-modal');
  }

  async confirmAction() {
    if (this.selectedOption === 'cancel') {
      await this.handleCancelAppointment();
    }
  }

  async handleCancelAppointment() {
    // make button ellipses button
    const continueButton = document.querySelector('.modal__continue');
    continueButton.classList.add('loading');

    try {
      // submit call for fetch
      await this.luminisServiceUtils.cancelAppointment(this.activeVisit.csn, this.errorRedirectPage);
    } catch {
      return;
    }

    const appointmentChangedConfirmation = document.querySelector(
      '.field--name-field-appointment-changed-confir .field__item',
    ).innerHTML;
    this.updateModalView(appointmentChangedConfirmation);

    // hide buttons
    document
      .querySelector('.edit-appointment-modal__content .appointment')
      .classList.add('strike-through');
    continueButton.classList.add('hidden');
    const backButton = document.querySelector(
      '.edit-appointment-modal .back-link',
    );
    backButton.classList.add('hidden');

    // show book again button
    const bookAgain = document.querySelector('.modal__book_again');
    bookAgain.classList.remove('hidden');
    const currentMnemonic = this.activeVisit.visitReasonMnemonic;
    bookAgain.addEventListener('click', async () => {
      await this.bookAgainListener(bookAgain, currentMnemonic);
    });

    // update next visit on dashboard
    await this.updateCallback();
  }

  async bookAgainListener(bookAgain, mnemonic) {
    bookAgain.classList.add('loading');
    if (mnemonic) {
      const canBookAgain = await this.luminisServiceUtils.checkBookAgain(
        mnemonic, this.errorRedirectPage
      );
      if (canBookAgain.hasAvailability) {
        // if /Schedule/AvailabilityInfo returns true send to Scheduling Results
        const schedulingResultsLink = document.querySelector(
          '.edit-appointment-modal .field--name-field-scheduling-results-link a',
        );
        window.location = `${schedulingResultsLink.href}?mnemonic=${mnemonic}&hasReferral=true`;
      } else {
        DashboardEditAppointmentModal.redirectToFindCareLanding();
      }
    } else {
      DashboardEditAppointmentModal.redirectToFindCareLanding();
    }
  }

  static redirectToFindCareLanding() {
    // if returns false send to Find care landing
    const findCareLanding = document.querySelector(
      '.edit-appointment-modal .field--name-field-find-care-landing-page-lin a',
    );
    window.location = findCareLanding.href;
  }
}

(() => {})(Drupal);
